import { CloseIcon } from '@onbeefapp/constants/src/icons'
import React, { useEffect, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import cx from 'classnames'

export type ModalProps = {
  isOpen: boolean
  onClose: () => void
  position?: 'center' | 'right'
  children: React.ReactNode
  className?: string
  closeIconClassName?: string
  closeIcon?: boolean
  notClose?: boolean
  confirmClose?: boolean
  id?: string
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  position = 'center',
  children,
  className,
  closeIconClassName,
  closeIcon = true,
  notClose = false,
  confirmClose = false,
  id,
}) => {
  const transitionDuration = 300
  const modalContentRef = useRef<HTMLDivElement>(null)
  const [modalOpen, setModalOpen] = useState(false)

  const handleClose = (confirmation: boolean) => {
    const toConfirm = confirmation && confirmClose
    if (!notClose && !toConfirm) {
      setTimeout(() => setModalOpen(false), transitionDuration)
    }
    if (toConfirm) {
      Swal.fire({
        title: 'Confirmação',
        text: 'As alterações feitas até agora serão perdidas. Deseja continuar sem salvar?',
        confirmButtonText: 'Sair sem salvar',
        cancelButtonText: 'Permanecer na página',
        showCancelButton: true,
        customClass: {
          confirmButton:
            '!bg-primary !text-contrastText !py-2 !px-6 !rounded-lg !outline-none !ring-0',
        },
      }).then((res) => {
        if (res.isConfirmed) {
          onClose()
        }
      })
    } else {
      onClose()
    }
  }

  useEffect(() => {
    if (isOpen) {
      setModalOpen(true)
    } else {
      setTimeout(() => setModalOpen(false), transitionDuration)
    }
  }, [isOpen])

  const getPositionClasses = () => {
    switch (position) {
      case 'center':
        return `top-1/2 left-1/2 transform ${
          isOpen
            ? '-translate-x-1/2 -translate-y-1/2'
            : 'translate-x-1/2 translate-y-1/2'
        } rounded-lg`
      case 'right':
        return `top-1/2 right-0 transform -translate-y-1/2 h-screen ${
          modalOpen ? 'translate-x-0' : 'translate-x-full'
        }`
    }
  }

  return (
    <div
      className={`fixed top-0 left-0 w-full h-full flex items-center justify-center bg-gray-800 bg-opacity-50 z-50 ${
        isOpen ? 'block' : 'hidden'
      }`}
      id={id}
      onClick={() => handleClose(true)}
    >
      <div
        ref={modalContentRef}
        onClick={(e) => void e.stopPropagation()}
        className={cx(
          `bg-white ${getPositionClasses()} absolute overflow-y-auto overflow-x-hidden transition-transform duration-${transitionDuration}`,
          className
            ? className
            : `w-full ${
                position === 'center' ? 'max-h-[90vh] md:h-auto' : 'py-8'
              } sm:w-5/6 md:w-4/5 max-w-4xl`
        )}
      >
        {closeIcon && (
          <button
            type="button"
            onClick={() => handleClose(false)}
            className={cx(
              'absolute top-4 right-0 mr-8 z-40',
              closeIconClassName
            )}
          >
            <CloseIcon />
          </button>
        )}
        {children}
      </div>
    </div>
  )
}
